import React, { useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';

import { formatDuration } from '@agilytics/utils';
import { howLongWithThroughput, howManyWithThroughput } from '@agilytics/utils/metrics';
import { GridContainer, Paragraph, Button, Heading, Formatted, Spinner } from '@agilytics/components';
import { Share } from '@agilytics/containers';
import { useAnalytics } from '@agilytics/context/Analytics';

import howLongInstructions from './how-long-instructions.json';
import howManyInstructions from './how-many-instructions.json';
import throughputInstructions from './throughput-instructions.json';

const StyledInput = styled.input`
  font-family: ${({ theme }) => theme.fonts.main};
  font-size: ${({ theme }) => theme.fontSizes.paragraph};
  padding: ${({ theme }) => theme.spacingDouble};
  border: 2px solid ${({ theme }) => theme.colors.outline};
  line-height: normal;
  width: 100%;
  min-width: 95%;
  margin: auto auto ${({ theme }) => theme.spacingDouble} auto;
`;

const Container = styled.section`
  display: flex;
  flex-direction: column;
  margin: ${({ theme }) => theme.spacingDouble};
`;

const SimulateContainer = styled.section`
  margin: ${({ theme }) => theme.spacingDouble} auto;
  flex: 1 1 50%;
`;

const Row = styled.section`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  flex: 1 1;
  margin-bottom: ${({ theme }) => theme.spacingDouble};
`;

const Instructions = styled.article`
  padding: ${({ theme }) => theme.spacingDouble};
  padding-top: ${({ theme }) => theme.spacingBase};
  flex: 1 1 50%;
`;

const ErrorMessage = styled.article`
  flex: 1 1 100%;
  background: rgba(255, 0, 0, 0.2);
  border: 1px solid rgba(255, 0, 0, 0.5);
  font-family: ${({ theme }) => theme.fonts.main};
  font-size: ${({ theme }) => theme.fontSizes.paragraph};
  padding: ${({ theme }) => theme.spacingBase};
  line-height: normal;
  width: 100%;
  min-width: 95%;
  margin: auto auto ${({ theme }) => theme.spacingBase} auto;
  text-align: center;
`;

const StyledHeading = styled(Heading)`
  color: ${({ theme }) => theme.colors.bodyFg};
`;

const ConfidenceText = styled(Paragraph)`
  opacity: 0.5;
`;

const ANIMATION_TIMEOUT = 2500;

export default function Project() {
  const [issues, setIssues] = useState([]);
  const [howLongTime, setHowLong] = useState();
  const [howManyIssues, setHowMany] = useState();
  const [numIssues, setNumIssues] = useState();
  const [targetDate, setTargetDate] = useState();
  const [errorMessage, setError] = useState('');
  const [showHowLongResult, setShowHowLongResult] = useState(false);
  const [showHowManyResult, setShowHowManyResult] = useState(false);
  const { analytics } = useAnalytics();

  const numIssuesRef = React.createRef();
  const numDaysRef = React.createRef();
  const throughputRef = React.createRef();

  const simulateHowLong = e => {
    e.preventDefault();
    const numIssues = numIssuesRef.current.value;
    setNumIssues(numIssues);
    if (issues.length) {
      setShowHowLongResult(false);
      const result = howLongWithThroughput(numIssues, issues);
      analytics.track('demoSimulateHowLong', { category: 'simulate', label: `${numIssues} Issues`, value: result });
      setHowLong(result);
    } else {
      analytics.track('demoSimulateHowLong', { category: 'error', label: `${issues.length} Issues` });
    }
  };

  const simulateHowMany = e => {
    e.preventDefault();
    const numDays = moment(numDaysRef.current.value).diff(moment(), 'days');
    if (issues.length && numDays > 0) {
      setShowHowManyResult(false);
      setTargetDate(numDaysRef.current.value);
      const result = howManyWithThroughput(numDays, issues);
      analytics.track('demoSimulateHowMany', { category: 'simulate', label: `${numDays} Days`, value: result });
      setHowMany(result);
    } else {
      analytics.track('demoSimulateHowMany', { category: 'error', label: `${issues.length} Issues, ${numDays} Days` });
    }
  };

  const renderHowLongInput = () => {
    return (
      <SimulateContainer>
        <form onSubmit={simulateHowLong}>
          <StyledInput ref={numIssuesRef} type="number" placeholder={howLongInstructions.placeholder} required />
          <Button type="submit" size="l" block disabled={!issues.length}>
            {howLongInstructions.buttonText}
          </Button>
        </form>
      </SimulateContainer>
    );
  };

  const renderHowManyInput = () => {
    const tomorrowDate = moment().add(2, 'days').format('YYYY-MM-DD');
    return (
      <SimulateContainer>
        <form onSubmit={simulateHowMany}>
          <StyledInput ref={numDaysRef} type="date" min={tomorrowDate} required />
          <Button type="submit" size="l" block disabled={!issues.length}>
            {howManyInstructions.buttonText}
          </Button>
        </form>
      </SimulateContainer>
    );
  };

  const renderHowLongResult = () => {
    if (howLongTime !== undefined) {
      const endDate = moment().add(howLongTime, 'days');
      const duration = moment.duration(endDate.diff(moment()));
      if (!showHowLongResult) {
        setTimeout(() => setShowHowLongResult(true), ANIMATION_TIMEOUT);
      }
      return !showHowLongResult ? (
        <Spinner text="simulating..." />
      ) : (
        <React.Fragment>
          <StyledHeading level={1}>{endDate.format('MMMM Do YYYY').toLowerCase()}</StyledHeading>
          <Paragraph>
            is when you will complete <strong>{numIssues}</strong> tickets by. that is in{' '}
            <strong>{formatDuration(duration)}</strong>
          </Paragraph>
          <ConfidenceText>80% confidence</ConfidenceText>
        </React.Fragment>
      );
    }

    return <Formatted content={howLongInstructions.content} />;
  };
  const renderHowManyResult = () => {
    if (howManyIssues !== undefined) {
      const formattedTargetDate = moment(targetDate).format('dddd, MMMM Do YYYY').toLowerCase();
      if (!showHowManyResult) {
        setTimeout(() => setShowHowManyResult(true), ANIMATION_TIMEOUT);
      }
      return !showHowManyResult ? (
        <Spinner text="simulating..." />
      ) : (
        <React.Fragment>
          <StyledHeading level={1}>{howManyIssues} tickets</StyledHeading>
          <Paragraph>will be completed by {formattedTargetDate}</Paragraph>
          <ConfidenceText>80% confidence</ConfidenceText>
        </React.Fragment>
      );
    }
    return <Formatted content={howManyInstructions.content} />;
  };

  const setThroughput = e => {
    setError('');
    e.preventDefault();
    const throughput = throughputRef.current.value.split(',').map(num => Math.round(Math.abs(parseInt(num.trim()))));

    if (throughput.some(isNaN)) {
      return setError('you must use numbers for throughput in the format: 1,2,3,4,5');
    }
    if (throughput.length < 11) {
      return setError('you must include at least 11 days of throughput for a forecast to be made');
    }
    setIssues(throughput);

    analytics.track('demoSetThroughput', { category: 'simulate', label: `Throughput: ${throughput.join(',')}` });
  };

  const renderThroughputInput = () => {
    return (
      <SimulateContainer>
        <form onSubmit={setThroughput}>
          <StyledInput ref={throughputRef} placeholder="1,12,0,0,3,7,2,0,6,8,3" required />
          <Button type="submit" size="l" block>
            {throughputInstructions.buttonText}
          </Button>
        </form>
      </SimulateContainer>
    );
  };

  return (
    <GridContainer>
      <Container>
        {errorMessage.length ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}
        <Heading level={3}>{throughputInstructions.title}</Heading>
        <Row>
          {renderThroughputInput()}
          <Instructions>
            {' '}
            <Formatted content={throughputInstructions.content} />
          </Instructions>
        </Row>
        <Heading level={3}>{howLongInstructions.title}</Heading>
        <Row>
          {renderHowLongInput()}
          <Instructions>{renderHowLongResult()}</Instructions>
        </Row>
        <Heading level={3}>{howManyInstructions.title}</Heading>
        <Row>
          {renderHowManyInput()}
          <Instructions>{renderHowManyResult()}</Instructions>
        </Row>
        <Row>
          <Share />
        </Row>
      </Container>
    </GridContainer>
  );
}
